import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Users, BookOpen, Brain, TrendingUp, AlertTriangle,
  Filter, Calendar, Target, ChevronRight, Loader2, X,
  BarChart3, Activity, Zap, FileText, ArrowUpRight, ArrowDownRight,
  RefreshCw, Sparkles, Lightbulb, Plus, Edit, Trash2, Eye,
  Check, Clock, AlertCircle, Download, Info, HelpCircle,
  Heart, Crown, Flag, RotateCcw, Radar, MapPin, ChevronDown, ChevronUp,
  ClipboardList, Lock, Unlock, LogIn, UserX, Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import EmailHealthCard from '../components/admin/EmailHealthCard';
import { FeatureMap, TechArchitecture } from '../components/admin/AdminSaudeSections';
import { HumanoPontuais } from '../components/admin/HumanoPontuais';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Super admin login uses stored session - no email hardcoded for security

export default function AdminSaudeApp() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('30days');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  
  // Admin login state
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // AI Analysis state
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  // Campaign modal state
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    type: 'pontual',
    start_date: '',
    end_date: '',
    description: '',
    objective: '',
    goal: ''
  });

  // Detail drawer state
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Sparkline data state
  const [sparklineData, setSparklineData] = useState({});
  const [sparklineLoading, setSparklineLoading] = useState({});

  // Export loading state
  const [exportLoading, setExportLoading] = useState(false);

  // 2FA Management state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [users2FAStatus, setUsers2FAStatus] = useState([]);
  const [users2FALoading, setUsers2FALoading] = useState(false);
  const [search2FA, setSearch2FA] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  // Health Index state
  const [healthIndex, setHealthIndex] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [showHealthDetail, setShowHealthDetail] = useState(false);

  // Opportunities state
  const [opportunities, setOpportunities] = useState(null);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [showOpportunityDrawer, setShowOpportunityDrawer] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityDetails, setOpportunityDetails] = useState(null);
  const [opportunityDetailsLoading, setOpportunityDetailsLoading] = useState(false);

  // Abandonment Map state
  const [abandonmentMap, setAbandonmentMap] = useState(null);
  const [abandonmentLoading, setAbandonmentLoading] = useState(false);
  const [showAbandonmentDrawer, setShowAbandonmentDrawer] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageDetails, setStageDetails] = useState(null);
  const [stageDetailsLoading, setStageDetailsLoading] = useState(false);

  // Feature Map & Tech Architecture sections state
  const [featureMapExpanded, setFeatureMapExpanded] = useState(false);
  const [techArchExpanded, setTechArchExpanded] = useState(false);
  const [humanoPontuaisExpanded, setHumanoPontuaisExpanded] = useState(false);

  const periodOptions = [
    { value: 'today', label: 'Hoje' },
    { value: '7days', label: 'Últimos 7 dias' },
    { value: '30days', label: 'Últimos 30 dias' },
    { value: 'month', label: 'Mês atual' },
    { value: 'prevmonth', label: 'Mês anterior' },
    { value: 'year', label: 'Ano atual' }
  ];

  const campaignTypes = [
    { value: 'pontual', label: 'Pontual' },
    { value: 'curto_prazo', label: 'Curto prazo' },
    { value: 'longo_prazo', label: 'Longo prazo' }
  ];

  useEffect(() => {
    verifyAccess();
  }, []);

  useEffect(() => {
    if (authorized) {
      fetchMetrics();
      fetchCampaigns();
      fetchHealthIndex();
      fetchOpportunities();
      fetchAbandonmentMap();
    }
  }, [authorized, period, selectedCampaign]);

  const verifyAccess = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/verify`, {
        withCredentials: true
      });
      setAuthorized(true);
      setAdminUser(response.data.user);
    } catch (error) {
      console.error('Access denied:', error);
      setAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/metrics`, {
        params: { period, campaign_id: selectedCampaign !== 'all' ? selectedCampaign : null },
        withCredentials: true
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Erro ao carregar métricas');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns`, {
        withCredentials: true
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  // Fetch sparkline data for a metric
  const fetchSparkline = async (metricType) => {
    setSparklineLoading(prev => ({ ...prev, [metricType]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/admin/sparkline/${metricType}`, {
        params: { period },
        withCredentials: true
      });
      setSparklineData(prev => ({ ...prev, [metricType]: response.data }));
    } catch (error) {
      console.error(`Error fetching sparkline for ${metricType}:`, error);
    } finally {
      setSparklineLoading(prev => ({ ...prev, [metricType]: false }));
    }
  };

  // Fetch all sparklines when metrics change
  useEffect(() => {
    if (metrics) {
      ['users', 'books', 'ai', 'errors'].forEach(metric => {
        fetchSparkline(metric);
      });
    }
  }, [metrics, period]);

  // Export to CSV
  const handleExport = async (type) => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/${type}`, {
        params: { period },
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_${type}_${new Date().toISOString().slice(0,10)}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Exportação concluída!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar dados');
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch Health Index
  const fetchHealthIndex = async () => {
    setHealthLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/health-index`, {
        params: { period },
        withCredentials: true
      });
      setHealthIndex(response.data);
    } catch (error) {
      console.error('Error fetching health index:', error);
    } finally {
      setHealthLoading(false);
    }
  };

  // Fetch Opportunities
  const fetchOpportunities = async () => {
    setOpportunitiesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/opportunities`, {
        params: { period },
        withCredentials: true
      });
      setOpportunities(response.data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  // Fetch Opportunity Details
  const fetchOpportunityDetails = async (opportunityId) => {
    setOpportunityDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/opportunities/${opportunityId}`, {
        withCredentials: true
      });
      setOpportunityDetails(response.data);
    } catch (error) {
      console.error('Error fetching opportunity details:', error);
      setOpportunityDetails({ items: [], error: 'Erro ao carregar detalhes' });
    } finally {
      setOpportunityDetailsLoading(false);
    }
  };

  // Fetch Abandonment Map
  const fetchAbandonmentMap = async () => {
    setAbandonmentLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/abandonment-map`, {
        params: { period },
        withCredentials: true
      });
      setAbandonmentMap(response.data);
    } catch (error) {
      console.error('Error fetching abandonment map:', error);
    } finally {
      setAbandonmentLoading(false);
    }
  };

  // Fetch Stage Details for Abandonment Map
  const fetchStageDetails = async (stageId) => {
    setStageDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/abandonment-map/${stageId}`, {
        withCredentials: true
      });
      setStageDetails(response.data);
    } catch (error) {
      console.error('Error fetching stage details:', error);
      setStageDetails({ items: [], error: 'Erro ao carregar detalhes' });
    } finally {
      setStageDetailsLoading(false);
    }
  };

  // Open opportunity drawer
  const openOpportunityDrawer = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowOpportunityDrawer(true);
    fetchOpportunityDetails(opportunity.id);
  };

  // Open abandonment stage drawer
  const openStageDrawer = (stage) => {
    setSelectedStage(stage);
    setShowAbandonmentDrawer(true);
    fetchStageDetails(stage.id);
  };

  const runAIAnalysis = async (type) => {
    setDrawerType(type);
    setShowDrawer(true);
    setAiLoading(true);
    setAiAnalysis(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/intelligence/analyze`,
        {
          analysis_type: type,
          filters: { period, campaign_id: selectedCampaign !== 'all' ? selectedCampaign : null }
        },
        { withCredentials: true }
      );
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erro ao gerar análise');
      setAiAnalysis('Erro ao gerar análise. Tente novamente.');
    } finally {
      setAiLoading(false);
    }
  };

  // Campaign CRUD
  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    
    if (!campaignForm.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      if (editingCampaign) {
        await axios.put(
          `${API_URL}/api/admin/campaigns/${editingCampaign.campaign_id}`,
          campaignForm,
          { withCredentials: true }
        );
        toast.success('Campanha atualizada!');
      } else {
        await axios.post(
          `${API_URL}/api/admin/campaigns`,
          campaignForm,
          { withCredentials: true }
        );
        toast.success('Campanha criada!');
      }
      
      setShowCampaignModal(false);
      setEditingCampaign(null);
      setCampaignForm({
        title: '', type: 'pontual', start_date: '', end_date: '',
        description: '', objective: '', goal: ''
      });
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Erro ao salvar campanha');
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      title: campaign.title || '',
      type: campaign.type || 'pontual',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      description: campaign.description || '',
      objective: campaign.objective || '',
      goal: campaign.goal || ''
    });
    setShowCampaignModal(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/campaigns/${campaignId}`, {
        withCredentials: true
      });
      toast.success('Campanha excluída!');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Erro ao excluir campanha');
    }
  };

  // Detail drawer
  const openDetailDrawer = async (type) => {
    setDetailType(type);
    setShowDetailDrawer(true);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const response = await axios.get(`${API_URL}/api/admin/details/${type}`, {
        params: { period },
        withCredentials: true
      });
      setDetailData(response.data);
    } catch (error) {
      console.error('Error fetching details:', error);
      setDetailData({ error: 'Erro ao carregar detalhes' });
    } finally {
      setDetailLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString('pt-BR') || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const ChangeIndicator = ({ value, tooltip }) => {
    if (value === 0) return <span className="text-muted-foreground text-sm">0%</span>;
    const isPositive = value > 0;
    const content = (
      <span className={`flex items-center text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        {Math.abs(value)}%
      </span>
    );
    
    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">{content}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return content;
  };

  // Sparkline mini chart component
  const SparklineChart = ({ data, color = "#f59e0b" }) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div className="mt-2" style={{ width: '100%', height: 48 }}>
        <ResponsiveContainer width="100%" height={48}>
          <LineChart data={data}>
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={color}
              strokeWidth={2}
              dot={false}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: '#1c1917',
                border: '1px solid #44403c',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              labelStyle={{ color: '#a8a29e' }}
              formatter={(value) => [value, 'Total']}
              labelFormatter={(label) => `Data: ${label}`}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Info tooltip helper
  const InfoTooltip = ({ text }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle size={14} className="text-stone-500 cursor-help ml-1" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs max-w-xs">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const MetricCard = ({ icon: Icon, color, title, children, onClick, sparkline, sparklineColor, tooltip }) => (
    <div 
      className={`bg-stone-900/50 rounded-xl border border-stone-800 p-5 ${onClick ? 'cursor-pointer hover:border-stone-700 transition-colors' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} />
          <h3 className="font-semibold">{title}</h3>
          {tooltip && <InfoTooltip text={tooltip} />}
        </div>
        {onClick && <ChevronRight size={16} className="text-stone-500" />}
      </div>
      {children}
      {sparkline && sparkline.data && sparkline.data.length > 0 && (
        <SparklineChart data={sparkline.data} color={sparklineColor} />
      )}
    </div>
  );

  // Health Index colors
  const getHealthColor = (classification) => {
    const colors = {
      'Excelente': { bg: 'from-green-500/20 to-emerald-500/10', border: 'border-green-500/30', text: 'text-green-400', ring: 'ring-green-500' },
      'Bom com atenção': { bg: 'from-yellow-500/20 to-amber-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', ring: 'ring-yellow-500' },
      'Atenção': { bg: 'from-orange-500/20 to-red-500/10', border: 'border-orange-500/30', text: 'text-orange-400', ring: 'ring-orange-500' },
      'Crítico': { bg: 'from-red-500/20 to-red-600/10', border: 'border-red-500/30', text: 'text-red-400', ring: 'ring-red-500' }
    };
    return colors[classification] || colors['Atenção'];
  };

  // Opportunity icon mapping
  const getOpportunityIcon = (iconName) => {
    const icons = {
      'crown': Crown,
      'flag': Flag,
      'sparkles': Sparkles,
      'rotate-ccw': RotateCcw,
      'trending-up': TrendingUp
    };
    return icons[iconName] || Target;
  };

  // ============================================
  // 2FA MANAGEMENT FUNCTIONS
  // ============================================
  
  const fetchUsers2FAStatus = async (searchQuery = '') => {
    setUsers2FALoading(true);
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`${API_URL}/api/admin/users/2fa-status${params}`, {
        withCredentials: true
      });
      setUsers2FAStatus(response.data);
    } catch (error) {
      toast.error('Erro ao buscar status de 2FA');
    } finally {
      setUsers2FALoading(false);
    }
  };

  const create2FABypass = async (userId, userEmail) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'bypass' }));
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/2fa-bypass`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchUsers2FAStatus(search2FA);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar bypass');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const remove2FABypass = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'removing' }));
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}/2fa-bypass`, {
        withCredentials: true
      });
      toast.success('Bypass removido');
      fetchUsers2FAStatus(search2FA);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover bypass');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const reset2FA = async (userId, userEmail) => {
    if (!window.confirm(`Resetar 2FA de ${userEmail}? O usuário precisará configurar novamente.`)) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [userId]: 'resetting' }));
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-2fa`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchUsers2FAStatus(search2FA);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar 2FA');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const open2FAModal = () => {
    setShow2FAModal(true);
    fetchUsers2FAStatus();
  };

  // Admin login handler - requires both email and password for security
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminEmail.trim() || !adminPassword) {
      setLoginError('Preencha email e senha');
      return;
    }
    
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: adminEmail.trim(),
        password: adminPassword
      }, { withCredentials: true });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setLoginError('2FA necessário. Faça login pela página principal.');
        return;
      }
      
      // Re-verify access
      await verifyAccess();
    } catch (error) {
      setLoginError('Credenciais incorretas ou sem permissão');
    } finally {
      setLoginLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Access denied - Show minimal login
  if (!authorized) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
        <div className="w-full max-w-xs">
          {/* Logo */}
          <div className="flex justify-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Key className="w-8 h-8 text-stone-900" />
            </div>
          </div>
          
          {/* Login Form - requires email and password */}
          <form onSubmit={handleAdminLogin} className="space-y-3">
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@email.com"
              className="w-full bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 placeholder-stone-600"
              autoFocus
            />
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 placeholder-stone-600"
            />
            
            {loginError && (
              <p className="text-red-400 text-sm text-center mt-3">{loginError}</p>
            )}
            
            <Button
              type="submit"
              disabled={loginLoading || !adminPassword || !adminEmail.trim()}
              className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-stone-900 h-12 rounded-xl font-medium"
            >
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Entrar'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      {/* Header */}
      <header className="border-b border-stone-800 bg-stone-900/50 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Activity className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Saúde do App</h1>
                <p className="text-sm text-stone-400">Painel Administrativo</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-400">
                {adminUser?.name} • <span className="text-amber-500">Super Admin</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchMetrics}
                className="text-stone-400 hover:text-white"
              >
                <RefreshCw size={16} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-stone-900/50 rounded-xl border border-stone-800">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-stone-400" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
            >
              {periodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Target size={18} className="text-stone-400" />
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="all">Todas as Campanhas</option>
              {campaigns.map(camp => (
                <option key={camp.campaign_id} value={camp.campaign_id}>{camp.title}</option>
              ))}
            </select>
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditingCampaign(null);
              setCampaignForm({
                title: '', type: 'pontual', start_date: '', end_date: '',
                description: '', objective: '', goal: ''
              });
              setShowCampaignModal(true);
            }}
            className="border-stone-700 text-stone-300 hover:bg-stone-800"
          >
            <Plus size={14} className="mr-1" />
            Nova Campanha
          </Button>
        </div>

        {/* Índice de Saúde do Author's Key */}
        {healthIndex && (
          <div 
            className={`mb-6 p-6 bg-gradient-to-br ${getHealthColor(healthIndex.classification).bg} rounded-xl border ${getHealthColor(healthIndex.classification).border} cursor-pointer hover:opacity-90 transition-opacity`}
            onClick={() => setShowHealthDetail(!showHealthDetail)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full bg-stone-900/50 ${getHealthColor(healthIndex.classification).ring} ring-2`}>
                  <Heart className={`w-8 h-8 ${getHealthColor(healthIndex.classification).text}`} />
                </div>
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    Índice de Saúde do Author's Key
                    <InfoTooltip text="Score consolidado que reflete a saúde geral da plataforma (0-100)" />
                  </h2>
                  <p className="text-sm text-stone-400">Visão executiva consolidada</p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className={`text-4xl font-bold ${getHealthColor(healthIndex.classification).text}`}>
                      {healthIndex.index}
                    </span>
                    <span className="text-stone-400 text-lg">/100</span>
                  </div>
                  <p className={`text-sm font-medium ${getHealthColor(healthIndex.classification).text}`}>
                    {healthIndex.classification}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <ChangeIndicator value={healthIndex.change} tooltip="vs período anterior" />
                  {showHealthDetail ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>
            </div>

            {/* Detalhamento dos Pilares */}
            {showHealthDetail && (
              <div className="mt-6 pt-6 border-t border-stone-700/50">
                <h3 className="text-sm font-semibold text-stone-400 mb-4">DETALHAMENTO DOS PILARES</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {Object.entries(healthIndex.pillars).map(([key, pillar]) => (
                    <div key={key} className="p-4 bg-stone-900/50 rounded-lg border border-stone-700/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-stone-400 font-medium">{pillar.name}</span>
                        <span className="text-xs text-stone-500">{pillar.weight}%</span>
                      </div>
                      <div className="flex items-end gap-1">
                        <span className={`text-2xl font-bold ${pillar.score >= 70 ? 'text-green-400' : pillar.score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {pillar.score}
                        </span>
                        <span className="text-stone-500 text-sm mb-0.5">/100</span>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-2 h-1.5 bg-stone-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${pillar.score >= 70 ? 'bg-green-500' : pillar.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${pillar.score}%` }}
                        />
                      </div>
                      {pillar.metrics?.mocked && (
                        <p className="text-xs text-stone-500 mt-2 italic">Aguardando integração</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Central de Inteligência */}
        <div className="mb-8 p-6 bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-xl border border-amber-500/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Brain className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Central de Inteligência</h2>
              <p className="text-sm text-stone-400">Análise automática dos dados do painel</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => runAIAnalysis('audit')}
              className="bg-stone-800 hover:bg-stone-700 border border-stone-700"
            >
              <Sparkles className="mr-2" size={18} />
              Auditoria Completa
            </Button>
            <Button
              onClick={() => runAIAnalysis('strategy')}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
            >
              <Lightbulb className="mr-2" size={18} />
              Estratégia e Ações
            </Button>
          </div>
        </div>

        {/* Radar de Oportunidades */}
        {opportunities && opportunities.opportunities && (
          <div className="mb-8 p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 rounded-xl border border-cyan-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Radar className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  Radar de Oportunidades
                  <span className="px-2 py-0.5 bg-cyan-500/20 rounded-full text-xs text-cyan-400">
                    {opportunities.total} detectadas
                  </span>
                </h2>
                <p className="text-sm text-stone-400">Oportunidades de crescimento identificadas automaticamente</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {opportunities.opportunities.map((opp) => {
                const IconComponent = getOpportunityIcon(opp.icon);
                const colorClasses = {
                  amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
                  green: 'text-green-400 bg-green-500/10 border-green-500/20',
                  purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
                  orange: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
                  blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                };
                const colors = colorClasses[opp.color] || colorClasses.blue;
                
                return (
                  <div 
                    key={opp.id}
                    onClick={() => openOpportunityDrawer(opp)}
                    className={`p-4 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${colors}`}
                  >
                    <div className="flex items-start gap-3">
                      <IconComponent className="w-5 h-5 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-2xl">{opp.count}</span>
                          <ChevronRight size={16} className="text-stone-500" />
                        </div>
                        <p className="text-sm font-medium truncate">{opp.title}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Campaign Card (if selected) */}
        {selectedCampaign !== 'all' && campaigns.find(c => c.campaign_id === selectedCampaign) && (
          <div className="mb-6 p-5 bg-gradient-to-br from-purple-500/10 to-pink-500/5 rounded-xl border border-purple-500/20">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold">Campanha Ativa</h3>
                </div>
                {(() => {
                  const camp = campaigns.find(c => c.campaign_id === selectedCampaign);
                  return (
                    <div className="space-y-1">
                      <p className="text-lg font-medium">{camp.title}</p>
                      <p className="text-sm text-stone-400">
                        {camp.type} • {formatDate(camp.start_date)} → {formatDate(camp.end_date)}
                      </p>
                      {camp.objective && <p className="text-sm text-stone-300 mt-2">{camp.objective}</p>}
                      {camp.goal && (
                        <p className="text-sm text-amber-400 mt-1">Meta: {camp.goal}</p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEditCampaign(campaigns.find(c => c.campaign_id === selectedCampaign))}
                  className="text-stone-400 hover:text-white"
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* USUÁRIOS */}
            <MetricCard 
              icon={Users} 
              color="text-blue-400" 
              title="Usuários"
              onClick={() => openDetailDrawer('users')}
              sparkline={sparklineData.users}
              sparklineColor="#60a5fa"
              tooltip="Total de usuários cadastrados na plataforma"
            >
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold">{formatNumber(metrics.users.total)}</p>
                  <p className="text-sm text-stone-400">Total de usuários</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-stone-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-semibold">{formatNumber(metrics.users.new)}</p>
                      <ChangeIndicator 
                        value={metrics.users.new_change} 
                        tooltip={`vs período anterior (${metrics.users.new_prev || 0} novos)`}
                      />
                    </div>
                    <p className="text-xs text-stone-400">Novos no período</p>
                  </div>
                  <div className="p-3 bg-stone-800/50 rounded-lg">
                    <p className="text-xl font-semibold">{formatNumber(metrics.users.active_sessions)}</p>
                    <p className="text-xs text-stone-400">Sessões ativas</p>
                  </div>
                </div>
              </div>
            </MetricCard>

            {/* PRODUÇÃO LITERÁRIA */}
            <MetricCard 
              icon={BookOpen} 
              color="text-green-400" 
              title="Produção Literária"
              onClick={() => openDetailDrawer('books')}
              sparkline={sparklineData.books}
              sparklineColor="#4ade80"
              tooltip="Livros, capítulos e palavras escritas na plataforma"
            >
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold">{formatNumber(metrics.production.total_books)}</p>
                  <p className="text-sm text-stone-400">Total de livros</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{metrics.production.books_writing}</p>
                    <p className="text-xs text-stone-400">Em escrita</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{metrics.production.books_finished}</p>
                    <p className="text-xs text-stone-400">Finalizados</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{formatNumber(metrics.production.total_chapters)}</p>
                    <p className="text-xs text-stone-400">Capítulos</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{formatNumber(metrics.production.total_words)}</p>
                    <p className="text-xs text-stone-400">Palavras</p>
                  </div>
                </div>
              </div>
            </MetricCard>

            {/* USO DA IA */}
            <MetricCard 
              icon={Zap} 
              color="text-purple-400" 
              title="IA / Emulador"
              onClick={() => openDetailDrawer('ai')}
              sparkline={sparklineData.ai}
              sparklineColor="#c084fc"
              tooltip="Uso do Emulador Acelerador e gerações com IA"
            >
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold">{formatNumber(metrics.ai_usage.generations_period)}</p>
                  <p className="text-sm text-stone-400">Gerações com IA</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-stone-800/50 rounded-lg">
                    <p className="text-xl font-semibold">{metrics.ai_usage.emulator_users}</p>
                    <p className="text-xs text-stone-400">Usuários Emulador</p>
                  </div>
                  <div className="p-3 bg-stone-800/50 rounded-lg">
                    <div className="flex items-center gap-1">
                      <p className="text-xl font-semibold">{metrics.ai_usage.adoption_rate}%</p>
                      <InfoTooltip text="Porcentagem de usuários que ativaram o Emulador" />
                    </div>
                    <p className="text-xs text-stone-400">Taxa de adoção</p>
                  </div>
                </div>
              </div>
            </MetricCard>

            {/* FUNIL DO AUTOR */}
            <MetricCard icon={TrendingUp} color="text-amber-400" title="Funil do Autor">
              <div className="space-y-2">
                {[
                  { label: 'Cadastrados', value: metrics.funnel.registered, pct: 100 },
                  { label: 'Criaram livro', value: metrics.funnel.created_book, pct: Math.round((metrics.funnel.created_book / Math.max(metrics.funnel.registered, 1)) * 100) },
                  { label: 'Escreveram capítulo', value: metrics.funnel.wrote_chapter, pct: Math.round((metrics.funnel.wrote_chapter / Math.max(metrics.funnel.registered, 1)) * 100) },
                  { label: 'Finalizaram livro', value: metrics.funnel.finished_book, pct: Math.round((metrics.funnel.finished_book / Math.max(metrics.funnel.registered, 1)) * 100) },
                  { label: 'Geraram ebook', value: metrics.funnel.generated_ebook || 0, pct: Math.round(((metrics.funnel.generated_ebook || 0) / Math.max(metrics.funnel.registered, 1)) * 100) }
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-stone-300">{step.label}</span>
                        <span className="text-stone-400">{step.value}</span>
                      </div>
                      <div className="h-2 bg-stone-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                          style={{ width: `${step.pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-stone-500 w-10 text-right">{step.pct}%</span>
                  </div>
                ))}
              </div>
            </MetricCard>

            {/* FINANCEIRO */}
            <MetricCard icon={BarChart3} color="text-emerald-400" title="Financeiro">
              <div className="space-y-4 opacity-60">
                <div>
                  <p className="text-3xl font-bold text-stone-500">R$ --</p>
                  <p className="text-sm text-stone-500">Receita total</p>
                </div>
                <div className="p-3 bg-stone-800/30 rounded-lg">
                  <p className="text-xs text-stone-500 flex items-center gap-2">
                    <Clock size={12} />
                    Integração com pagamentos pendente
                  </p>
                </div>
              </div>
            </MetricCard>

            {/* ERROS */}
            <MetricCard 
              icon={AlertTriangle} 
              color="text-red-400" 
              title="Erros da Plataforma"
              onClick={() => openDetailDrawer('errors')}
              sparkline={sparklineData.errors}
              sparklineColor="#f87171"
              tooltip="Falhas de login, erros de sistema e eventos críticos"
            >
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-bold">{metrics.errors.total_errors}</p>
                  <p className="text-sm text-stone-400">Total de erros</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-red-500/10 rounded-lg text-center border border-red-500/20">
                    <p className="text-lg font-semibold text-red-400">{metrics.errors.failed_logins}</p>
                    <p className="text-xs text-stone-400">Falhas login</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{metrics.errors.ai_errors}</p>
                    <p className="text-xs text-stone-400">Erros IA</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{metrics.errors.editor_errors}</p>
                    <p className="text-xs text-stone-400">Erros editor</p>
                  </div>
                  <div className="p-2 bg-stone-800/50 rounded-lg text-center">
                    <p className="text-lg font-semibold">{metrics.errors.critical_errors}</p>
                    <p className="text-xs text-stone-400">Críticos</p>
                  </div>
                </div>
              </div>
            </MetricCard>

            {/* COMUNICAÇÃO POR E-MAIL */}
            <EmailHealthCard period={period === '7days' ? '7d' : period === '90days' ? '90d' : '30d'} />

          </div>
        )}

        {/* Mapa de Funcionalidades */}
        <div className="mt-6">
          <FeatureMap 
            isExpanded={featureMapExpanded} 
            onToggle={() => setFeatureMapExpanded(!featureMapExpanded)} 
          />
        </div>

        {/* Arquitetura Técnica */}
        <div className="mt-6">
          <TechArchitecture 
            isExpanded={techArchExpanded} 
            onToggle={() => setTechArchExpanded(!techArchExpanded)} 
          />
        </div>

        {/* Humano Pontuais - Registro de Bugs/Melhorias */}
        <div className="mt-6">
          <HumanoPontuais
            isExpanded={humanoPontuaisExpanded}
            onToggle={() => setHumanoPontuaisExpanded(!humanoPontuaisExpanded)}
          />
        </div>

        {/* Card de Gestão de 2FA */}
        <div className="mt-6 p-6 bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-xl border border-amber-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Gestão de 2FA</h2>
                <p className="text-sm text-stone-400">Gerenciar autenticação de dois fatores dos usuários</p>
              </div>
            </div>
            <Button
              onClick={open2FAModal}
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
            >
              <Users size={16} className="mr-2" />
              Gerenciar Usuários
            </Button>
          </div>
        </div>

        {/* Mapa de Abandono da Escrita */}
        {abandonmentMap && (
          <div className="mt-8 p-6 bg-gradient-to-br from-rose-500/10 to-pink-500/5 rounded-xl border border-rose-500/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rose-500/20 rounded-lg">
                <MapPin className="w-6 h-6 text-rose-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  Mapa de Abandono da Escrita
                  <InfoTooltip text="Análise de onde os autores abandonam a jornada de escrita" />
                </h2>
                <p className="text-sm text-stone-400">Taxa de conversão total: {abandonmentMap.conversion_rate}%</p>
              </div>
              {abandonmentMap.biggest_dropout && (
                <div className="text-right">
                  <p className="text-xs text-stone-500">Maior ponto de abandono</p>
                  <p className="text-sm font-medium text-rose-400">
                    {abandonmentMap.biggest_dropout.stage} ({abandonmentMap.biggest_dropout.rate}%)
                  </p>
                </div>
              )}
            </div>

            {/* Funnel Visual */}
            <div className="space-y-2">
              {abandonmentMap.stages.map((stage, index) => {
                const isClickable = stage.dropout_count > 0 && stage.id !== 'registered' && stage.id !== 'ebook';
                return (
                  <div 
                    key={stage.id}
                    className={`relative ${isClickable ? 'cursor-pointer hover:opacity-90' : ''}`}
                    onClick={() => isClickable && openStageDrawer(stage)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Stage bar */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-stone-300 flex items-center gap-2">
                            {stage.name}
                            {isClickable && <ChevronRight size={14} className="text-stone-500" />}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">{stage.count}</span>
                            <span className="text-xs text-stone-500 w-12 text-right">{stage.percentage}%</span>
                          </div>
                        </div>
                        <div className="h-8 bg-stone-800 rounded-lg overflow-hidden relative">
                          <div 
                            className="h-full bg-gradient-to-r from-rose-500/80 to-pink-500/80 rounded-lg transition-all"
                            style={{ width: `${stage.percentage}%` }}
                          />
                          {/* Dropout indicator */}
                          {stage.dropout_rate > 0 && index > 0 && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                              <ArrowDownRight size={12} className="text-red-400" />
                              <span className="text-xs text-red-400 font-medium">-{stage.dropout_rate}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-stone-700/50 flex items-center justify-between">
              <p className="text-xs text-stone-500">
                Clique em uma etapa com abandono para ver os usuários afetados
              </p>
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <div className="w-3 h-3 bg-gradient-to-r from-rose-500 to-pink-500 rounded"></div>
                Taxa de conversão
                <div className="w-3 h-3 bg-red-400 rounded ml-2"></div>
                Taxa de abandono
              </div>
            </div>
          </div>
        )}

        {/* Campaigns List */}
        {campaigns.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Campanhas ({campaigns.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map((camp) => (
                <div 
                  key={camp.campaign_id}
                  className="bg-stone-900/50 rounded-lg border border-stone-800 p-4 hover:border-stone-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{camp.title}</h4>
                      <p className="text-xs text-stone-500 mt-1">
                        {campaignTypes.find(t => t.value === camp.type)?.label || camp.type}
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        {formatDate(camp.start_date)} → {formatDate(camp.end_date)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditCampaign(camp)}
                        className="p-1.5 text-stone-500 hover:text-white hover:bg-stone-800 rounded"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(camp.campaign_id)}
                        className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* AI Analysis Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowDrawer(false)}
          />
          <div className="relative w-full max-w-xl bg-stone-900 border-l border-stone-800 h-full overflow-y-auto">
            <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {drawerType === 'audit' ? (
                  <Sparkles className="w-5 h-5 text-amber-500" />
                ) : (
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                )}
                <h2 className="text-lg font-bold">
                  {drawerType === 'audit' ? 'IA Auditora' : 'IA Estrategista'}
                </h2>
              </div>
              <button
                onClick={() => setShowDrawer(false)}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-4" />
                  <p className="text-stone-400">
                    {drawerType === 'audit' ? 'Analisando dados...' : 'Gerando recomendações...'}
                  </p>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-stone-300 leading-relaxed">
                    {aiAnalysis}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {showDetailDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowDetailDrawer(false)}
          />
          <div className="relative w-full max-w-xl bg-stone-900 border-l border-stone-800 h-full overflow-y-auto">
            <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-bold">
                  {detailType === 'users' && 'Detalhes de Usuários'}
                  {detailType === 'books' && 'Detalhes de Livros'}
                  {detailType === 'ai' && 'Detalhes de IA'}
                  {detailType === 'errors' && 'Detalhes de Erros'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExport(detailType)}
                  disabled={exportLoading}
                  className="border-stone-700 text-stone-300 hover:bg-stone-800"
                >
                  {exportLoading ? (
                    <Loader2 size={14} className="animate-spin mr-1" />
                  ) : (
                    <Download size={14} className="mr-1" />
                  )}
                  CSV
                </Button>
                <button
                  onClick={() => setShowDetailDrawer(false)}
                  className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Period indicator */}
            <div className="px-6 py-3 bg-stone-800/30 border-b border-stone-800">
              <p className="text-sm text-stone-400 flex items-center gap-2">
                <Calendar size={14} />
                Período: {periodOptions.find(p => p.value === period)?.label || period}
              </p>
            </div>

            <div className="p-6">
              {detailLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-4" />
                  <p className="text-stone-400">Carregando detalhes...</p>
                </div>
              ) : detailData?.error ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                  <p className="text-stone-400">{detailData.error}</p>
                </div>
              ) : detailData?.items ? (
                <div className="space-y-3">
                  <p className="text-sm text-stone-500 mb-4">
                    {detailData.total || detailData.items.length} registros encontrados
                  </p>
                  {detailData.items.map((item, i) => (
                    <div key={i} className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.title || item.name || item.email}</p>
                          <p className="text-sm text-stone-400">{item.subtitle || item.status || ''}</p>
                        </div>
                        <span className="text-xs text-stone-500">{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  ))}
                  {detailData.items.length === 0 && (
                    <p className="text-center text-stone-500 py-8">Nenhum registro encontrado</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-stone-500 py-8">Dados não disponíveis</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowCampaignModal(false)}
          />
          <div className="relative w-full max-w-lg bg-stone-900 border border-stone-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}
              </h2>
              <button
                onClick={() => setShowCampaignModal(false)}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveCampaign} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título *</label>
                <Input
                  value={campaignForm.title}
                  onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })}
                  placeholder="Nome da campanha"
                  className="bg-stone-800 border-stone-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo</label>
                <select
                  value={campaignForm.type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, type: e.target.value })}
                  className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm"
                >
                  {campaignTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Data Início</label>
                  <Input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                    className="bg-stone-800 border-stone-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Data Fim</label>
                  <Input
                    type="date"
                    value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                    className="bg-stone-800 border-stone-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrição</label>
                <Textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  placeholder="Breve descrição da campanha"
                  rows={2}
                  className="bg-stone-800 border-stone-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Objetivo</label>
                <Input
                  value={campaignForm.objective}
                  onChange={(e) => setCampaignForm({ ...campaignForm, objective: e.target.value })}
                  placeholder="Ex: Aumentar engajamento"
                  className="bg-stone-800 border-stone-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Meta</label>
                <Input
                  value={campaignForm.goal}
                  onChange={(e) => setCampaignForm({ ...campaignForm, goal: e.target.value })}
                  placeholder="Ex: +25% de capítulos escritos"
                  className="bg-stone-800 border-stone-700"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-stone-900"
                >
                  <Check size={16} className="mr-2" />
                  {editingCampaign ? 'Salvar Alterações' : 'Criar Campanha'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCampaignModal(false)}
                  className="border-stone-700"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Opportunity Drawer */}
      {showOpportunityDrawer && selectedOpportunity && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowOpportunityDrawer(false)}
          />
          <div className="relative w-full max-w-xl bg-stone-900 border-l border-stone-800 h-full overflow-y-auto">
            <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const IconComponent = getOpportunityIcon(selectedOpportunity.icon);
                  return <IconComponent className={`w-5 h-5 text-${selectedOpportunity.color}-400`} />;
                })()}
                <h2 className="text-lg font-bold">{selectedOpportunity.title}</h2>
              </div>
              <button
                onClick={() => setShowOpportunityDrawer(false)}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-stone-400 mb-4">{selectedOpportunity.description}</p>
              
              {opportunityDetailsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-4" />
                  <p className="text-stone-400">Carregando detalhes...</p>
                </div>
              ) : opportunityDetails?.items ? (
                <div className="space-y-3">
                  <p className="text-sm text-stone-500 mb-4">
                    {opportunityDetails.total || opportunityDetails.items.length} registros encontrados
                  </p>
                  {opportunityDetails.items.map((item, i) => (
                    <div key={i} className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.name || item.title || item.email}</p>
                          <p className="text-sm text-stone-400">
                            {item.email && item.email}
                            {item.books && ` • ${item.books} livros`}
                            {item.chapters && ` • ${item.chapters} capítulos`}
                            {item.status && ` • ${item.status}`}
                          </p>
                        </div>
                        <span className="text-xs text-stone-500">
                          {item.days_inactive ? `${item.days_inactive} dias inativo` : formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {opportunityDetails.items.length === 0 && (
                    <p className="text-center text-stone-500 py-8">Nenhum registro encontrado</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-stone-500 py-8">Dados não disponíveis</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Abandonment Stage Drawer */}
      {showAbandonmentDrawer && selectedStage && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowAbandonmentDrawer(false)}
          />
          <div className="relative w-full max-w-xl bg-stone-900 border-l border-stone-800 h-full overflow-y-auto">
            <div className="sticky top-0 bg-stone-900 border-b border-stone-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-rose-400" />
                <div>
                  <h2 className="text-lg font-bold">Abandono: {selectedStage.name}</h2>
                  <p className="text-sm text-stone-400">{selectedStage.dropout_count} usuários abandonaram nesta etapa</p>
                </div>
              </div>
              <button
                onClick={() => setShowAbandonmentDrawer(false)}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {stageDetailsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-4" />
                  <p className="text-stone-400">Carregando detalhes...</p>
                </div>
              ) : stageDetails?.items ? (
                <div className="space-y-3">
                  <p className="text-sm text-stone-500 mb-4">
                    {stageDetails.total || stageDetails.items.length} usuários nesta situação
                  </p>
                  {stageDetails.items.map((item, i) => (
                    <div key={i} className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.name || item.title}</p>
                          <p className="text-sm text-stone-400">
                            {item.email && item.email}
                            {item.chapters !== undefined && ` • ${item.chapters} capítulos`}
                            {item.books !== undefined && ` • ${item.books} livros`}
                          </p>
                          {item.status && (
                            <p className="text-xs text-rose-400 mt-1">{item.status}</p>
                          )}
                        </div>
                        <span className="text-xs text-stone-500">
                          {item.last_activity ? formatDate(item.last_activity) : formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {stageDetails.items.length === 0 && (
                    <p className="text-center text-stone-500 py-8">Nenhum registro encontrado</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-stone-500 py-8">Dados não disponíveis</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gestão de 2FA */}
      {show2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShow2FAModal(false)}
          />
          <div className="relative w-full max-w-2xl bg-stone-900 rounded-xl border border-stone-800 max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-stone-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-amber-500" />
                <div>
                  <h2 className="text-lg font-bold">Gestão de 2FA</h2>
                  <p className="text-sm text-stone-400">Gerenciar autenticação dos usuários</p>
                </div>
              </div>
              <button
                onClick={() => setShow2FAModal(false)}
                className="p-2 hover:bg-stone-800 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Search */}
            <div className="p-4 border-b border-stone-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Buscar por email ou nome..."
                  value={search2FA}
                  onChange={(e) => setSearch2FA(e.target.value)}
                  className="flex-1 px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
                <Button
                  onClick={() => fetchUsers2FAStatus(search2FA)}
                  disabled={users2FALoading}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                >
                  {users2FALoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                </Button>
              </div>
            </div>
            
            {/* Users List */}
            <div className="flex-1 overflow-y-auto p-4">
              {users2FALoading && users2FAStatus.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : users2FAStatus.length === 0 ? (
                <p className="text-center text-stone-500 py-8">
                  Nenhum usuário encontrado. Use a busca acima.
                </p>
              ) : (
                <div className="space-y-3">
                  {users2FAStatus.map((user) => (
                    <div 
                      key={user.user_id}
                      className="p-4 bg-stone-800/50 rounded-lg border border-stone-700"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium">{user.name || 'Sem nome'}</p>
                          <p className="text-sm text-stone-400">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {user.two_fa_enabled ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                              <Check size={12} /> 2FA Ativo
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                              <X size={12} /> 2FA Inativo
                            </span>
                          )}
                          {user.has_bypass && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                              <Clock size={12} /> Bypass Ativo
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {user.has_bypass ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => remove2FABypass(user.user_id)}
                            disabled={actionLoading[user.user_id]}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            {actionLoading[user.user_id] === 'removing' ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <X size={14} className="mr-1" />
                            )}
                            Remover Bypass
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => create2FABypass(user.user_id, user.email)}
                            disabled={actionLoading[user.user_id]}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          >
                            {actionLoading[user.user_id] === 'bypass' ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Clock size={14} className="mr-1" />
                            )}
                            Bypass 6 horas
                          </Button>
                        )}
                        
                        {user.two_fa_enabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reset2FA(user.user_id, user.email)}
                            disabled={actionLoading[user.user_id]}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            {actionLoading[user.user_id] === 'resetting' ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <RefreshCw size={14} className="mr-1" />
                            )}
                            Resetar 2FA
                          </Button>
                        )}
                      </div>
                      
                      {user.has_bypass && user.bypass_expires && (
                        <p className="text-xs text-stone-500 mt-2">
                          Bypass expira: {new Date(user.bypass_expires).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
